"use client";

/**
 * Interactive shell for the FAQ page. Every question and answer is rendered on
 * the server and passed in as children — these islands only track which topic
 * is selected and which answer is expanded, so the copy always ships in the
 * initial HTML.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { AnimateIn } from "@/components/ui/motion";

const ACCENT = "#2BA6E6";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay, ease: "easeOut" as const },
});

type FaqState = {
  activeCategory: string;
  selectCategory: (id: string) => void;
  openKey: string | null;
  toggleKey: (key: string) => void;
};

const FaqStateContext = createContext<FaqState | null>(null);

function useFaqState() {
  const state = useContext(FaqStateContext);
  if (!state) {
    throw new Error("FAQ components must be rendered inside <FaqStateProvider>");
  }
  return state;
}

export function FaqStateProvider({ children }: { children: ReactNode }) {
  const [activeCategory, setActiveCategory] = useState("all");
  const [openKey, setOpenKey] = useState<string | null>(null);

  const selectCategory = useCallback((id: string) => {
    setActiveCategory(id);
    setOpenKey(null);
  }, []);

  const toggleKey = useCallback((key: string) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  const value = useMemo(
    () => ({ activeCategory, selectCategory, openKey, toggleKey }),
    [activeCategory, selectCategory, openKey, toggleKey]
  );

  return (
    <FaqStateContext.Provider value={value}>{children}</FaqStateContext.Provider>
  );
}

export type FaqTab = {
  id: string;
  label: string;
  accentFrom: string;
  accentTo: string;
};

export function FaqCategoryTabs({ categories }: { categories: FaqTab[] }) {
  const { activeCategory, selectCategory } = useFaqState();

  return (
    <div className="py-1 flex gap-2 overflow-x-auto scrollbar-hide">
      <button
        type="button"
        onClick={() => selectCategory("all")}
        className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
          activeCategory === "all"
            ? "text-white border-transparent shadow-sm"
            : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300"
        }`}
        style={
          activeCategory === "all"
            ? { backgroundColor: ACCENT, borderColor: ACCENT }
            : {}
        }
      >
        All Topics
      </button>
      {categories.map((cat) => (
        <button
          type="button"
          key={cat.id}
          onClick={() => selectCategory(cat.id)}
          className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
            activeCategory === cat.id
              ? "text-white border-transparent shadow-sm"
              : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300"
          }`}
          style={
            activeCategory === cat.id
              ? {
                  background: `linear-gradient(135deg, ${cat.accentFrom}, ${cat.accentTo})`,
                  borderColor: "transparent",
                }
              : {}
          }
        >
          {cat.label}
        </button>
      ))}
    </div>
  );
}

export function FaqCategoryPanel({
  categoryId,
  children,
}: {
  categoryId: string;
  children: ReactNode;
}) {
  const { activeCategory } = useFaqState();
  const visible = activeCategory === "all" || activeCategory === categoryId;

  return <div className={visible ? undefined : "hidden"}>{children}</div>;
}

export function FaqAccordionItem({
  itemKey,
  question,
  accentFrom,
  accentTo,
  index,
  children,
}: {
  itemKey: string;
  question: string;
  accentFrom: string;
  accentTo: string;
  index: number;
  children: ReactNode;
}) {
  const { openKey, toggleKey } = useFaqState();
  const isOpen = openKey === itemKey;
  const domId = itemKey.replace(/[^a-zA-Z0-9]+/g, "-");
  const panelId = `faq-answer-${domId}`;
  const buttonId = `faq-question-${domId}`;

  return (
    <AnimateIn
      {...fadeUp(index * 0.04)}
      className={`rounded-xl border transition-all duration-200 overflow-hidden ${
        isOpen
          ? "border-[#2BA6E6]/40 shadow-md bg-white"
          : "border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm"
      }`}
    >
      <button
        id={buttonId}
        type="button"
        className="w-full text-left px-5 py-4 flex items-center gap-4 group"
        onClick={() => toggleKey(itemKey)}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm transition-transform duration-200 group-hover:scale-105"
          style={{
            background: isOpen
              ? `linear-gradient(135deg, ${accentFrom}, ${accentTo})`
              : "linear-gradient(135deg, #e5e7eb, #d1d5db)",
          }}
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </span>
        <span className="flex-1 font-semibold text-neutral-900 text-sm sm:text-base leading-snug pr-2">
          {question}
        </span>
        <span
          className="flex-shrink-0 transition-transform duration-[250ms] ease-in-out"
          style={{
            color: isOpen ? ACCENT : "#9ca3af",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ChevronDown className="w-5 h-5" />
        </span>
      </button>

      {/* Answer stays in the DOM at all times; the grid row collapses it. */}
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="grid transition-[grid-template-rows] duration-[280ms] ease-in-out"
        style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
      >
        <div
          className={`overflow-hidden transition-opacity duration-200 ${
            isOpen ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="px-5 pb-5 pt-0">
            <div className="ml-11 text-sm sm:text-[0.9375rem] text-neutral-600 leading-relaxed border-t border-neutral-100 pt-3">
              {children}
            </div>
          </div>
        </div>
      </div>
    </AnimateIn>
  );
}
